import React from 'react'
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa'
import styled from 'styled-components'

type Props = {
  page: number
  totalPages: number
  onPageChange: (page: number) => void
  prevLabel: string
  nextLabel: string
}

const pageNumbers = (page: number, totalPages: number): Array<number | 'ellipsis'> => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }

  const pages: Array<number | 'ellipsis'> = [1]
  const start = Math.max(2, page - 1)
  const end = Math.min(totalPages - 1, page + 1)

  if (start > 2) pages.push('ellipsis')
  for (let i = start; i <= end; i += 1) pages.push(i)
  if (end < totalPages - 1) pages.push('ellipsis')
  pages.push(totalPages)
  return pages
}

export const Pagination: React.FC<Props> = ({
  page,
  totalPages,
  onPageChange,
  prevLabel,
  nextLabel
}) => {
  const lastPage = Math.max(totalPages, 1)
  const safePage = Math.min(Math.max(page, 1), lastPage)
  const items = pageNumbers(safePage, lastPage)

  return (
    <Bar>
      <PageNav
        type="button"
        disabled={safePage <= 1}
        onClick={() => onPageChange(safePage - 1)}
        aria-label={prevLabel}
      >
        <FaChevronLeft />
      </PageNav>
      {items.map((item, index) =>
        item === 'ellipsis' ? (
          <Ellipsis key={`e-${index}`}>…</Ellipsis>
        ) : (
          <PageButton
            key={item}
            type="button"
            $active={item === safePage}
            onClick={() => onPageChange(item)}
          >
            {item}
          </PageButton>
        )
      )}
      <PageNav
        type="button"
        disabled={safePage >= lastPage}
        onClick={() => onPageChange(safePage + 1)}
        aria-label={nextLabel}
      >
        <FaChevronRight />
      </PageNav>
    </Bar>
  )
}

const Bar = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 0.2rem;
`

const PageNav = styled.button`
  min-width: 1.75rem;
  height: 1.75rem;
  padding: 0 0.35rem;
  border-radius: 0.35rem;
  border: 1px solid ${({ theme }) => theme.colors.borderSoft};
  background: ${({ theme }) => theme.colors.neutralWhite};
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: 0.75rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  svg {
    width: 0.7rem;
    height: 0.7rem;
  }
`

const PageButton = styled.button<{ $active: boolean }>`
  min-width: 1.75rem;
  height: 1.75rem;
  padding: 0 0.35rem;
  border-radius: 0.35rem;
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.borderSoft)};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.primary : theme.colors.neutralWhite};
  color: ${({ theme, $active }) => ($active ? theme.colors.neutralWhite : theme.colors.metalGray)};
  font-size: 0.75rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

const Ellipsis = styled.span`
  min-width: 1.5rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.iconGray};
`
